import { z } from 'zod';
import { persistedTimestampSchema } from '../shared/firestore-timestamp.schema.js';
import { processStepStatusSchema } from '../shared/shipment-primitives.schema.js';
/** Sous-ensemble des états techniques utilisables par une étape du webhook Stripe. */
export const webhookStageStatusSchema = processStepStatusSchema.extract([
    'pending',
    'success',
    'failed',
    'retrying',
    'skipped',
]);
/** Synthèse exposée au support et au client pour l’ensemble du traitement. */
export const webhookOverallStatusSchema = z.enum([
    'success',
    'partial',
    'failed',
    'retrying',
    'pending',
]);
/** Erreur sérialisable, avec l’information nécessaire pour décider d’un retry. */
export const webhookErrorSchema = z
    .object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    retriable: z.boolean(),
})
    .strict();
/** Trace d’exécution d’une étape, sans recopier ses identifiants métier. */
export const webhookStageSchema = z
    .object({
    status: webhookStageStatusSchema,
    timestamp: persistedTimestampSchema.optional(),
    error: webhookErrorSchema.optional(),
    retryCount: z.number().int().nonnegative().optional(),
    lastRetryAt: persistedTimestampSchema.optional(),
    nextRetryAt: persistedTimestampSchema.optional(),
})
    .strict();
/**
 * Suivi canonique du pipeline. Les identifiants Stripe/DPD et les URLs de label
 * vivent dans `paymentFlow` et `standardData`, pas dans cette trace d’exécution.
 */
export const webhookProcessingStatusSchema = z
    .object({
    overall: webhookOverallStatusSchema,
    actionRequired: z.boolean(),
    contactSupport: z.boolean(),
    lastUpdated: persistedTimestampSchema,
    createdAt: persistedTimestampSchema,
    userMessage: z.object({ fr: z.string(), en: z.string(), nl: z.string() }).strict(),
    stages: z
        .object({
        payment: webhookStageSchema,
        dpd: webhookStageSchema,
        collection: webhookStageSchema
            .extend({
            rejectedDates: z.array(z.string()).optional(),
            confirmedDate: z.string().optional(),
            needsRebooking: z.boolean().optional(),
        })
            .strict()
            .optional(),
        label: webhookStageSchema,
        notifications: z
            .object({ email: webhookStageSchema, sms: webhookStageSchema.optional() })
            .strict(),
    })
        .strict(),
})
    .strict();
/**
 * Déduit le statut global depuis les étapes. Le paiement est bloquant, tandis
 * qu’un label ou une notification en erreur après création DPD produit un succès partiel.
 */
export function calculateWebhookOverallStatus(status) {
    const { payment, dpd, label, notifications, collection } = status.stages;
    if (payment.status === 'failed')
        return 'failed';
    if (status.actionRequired && collection?.needsRebooking)
        return 'partial';
    if (dpd.status === 'retrying' || (dpd.status === 'failed' && dpd.error?.retriable))
        return 'retrying';
    if (dpd.status === 'failed')
        return 'failed';
    if (dpd.status === 'success' &&
        (label.status === 'failed' || notifications.email.status === 'failed'))
        return 'partial';
    if (dpd.status === 'success' && (label.status === 'success' || label.status === 'skipped'))
        return 'success';
    return 'pending';
}
export const webhookStatusPatchSchema = webhookProcessingStatusSchema
    .omit({ stages: true })
    .partial()
    .extend({ stages: webhookProcessingStatusSchema.shape.stages.partial().optional() })
    .strict();
/** Le patch remplace une étape complète et ne modifie jamais l’objet reçu. */
export function reduceWebhookStatus(status, patch, now = new Date().toISOString()) {
    const next = {
        ...status,
        ...patch,
        stages: { ...status.stages, ...patch.stages },
        lastUpdated: now,
    };
    return webhookProcessingStatusSchema.parse({
        ...next,
        overall: calculateWebhookOverallStatus(next),
    });
}
